<app-side-dialog (closeDialog)="onCloseClick()">
  <div dialogHeader>
    <div>
      <h2>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.title' | translate: { name: userGroup()?.name } }}</h2>
    </div>
  </div>

  <div class="dialog-content" dialogContent>
    <form class="custom-form custom-form-readonly flex flex-col h-full" [formGroup]="form">
      <div class="flex-1 overflow-y-auto pb-4">
        <mat-form-field class="w-full mb-4">
          <mat-label>{{ 'shared.id' | translate }}</mat-label>
          <input matInput formControlName="id" />
          <mat-error [control]="form.get('id')" />
        </mat-form-field>

        <mat-form-field class="w-full mb-4">
          <mat-label>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.name' | translate }}</mat-label>
          <input matInput formControlName="name" />
          <mat-error [control]="form.get('name')" />
        </mat-form-field>

        @if (store.subscriptions$()?.length) {
          <mat-form-field class="w-full mb-4">
            <mat-label>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.subscription' | translate }}</mat-label>
            <mat-select formControlName="subscriptionId" (selectionChange)="onSubscriptionChange($event.value)">
              @for (subscription of store.subscriptions$(); track subscription.id) {
                <mat-option [value]="subscription.id">
                  {{ subscription.labelKey ?? '' | translate }} ({{
                    subscription.maxSeats < 0 ? '∞' : subscription.maxSeats
                  }}
                  {{ 'pages.admin.userGroup.tabs.userGroups.dialog.seats' | translate }},
                  {{ subscription.maxCreatorChannels < 0 ? '∞' : subscription.maxCreatorChannels }}
                  {{ 'pages.admin.userGroup.tabs.userGroups.dialog.channels' | translate }},
                  {{ subscription.maxConcurrentCampaigns < 0 ? '∞' : subscription.maxConcurrentCampaigns }}
                  {{ 'pages.admin.userGroup.tabs.userGroups.dialog.concurrentCampaigns' | translate }})
                </mat-option>
              }
            </mat-select>
            <mat-error [control]="form.get('subscriptionId')" />
          </mat-form-field>
        } @else {
          <div class="w-full mb-4 h-12">
            <ngx-skeleton-loader
              class="h-12 w-full"
              [theme]="{ height: '48px', width: '100%', 'border-radius': '100vw' }"
            />
          </div>
        }

        <div class="grid grid-cols-2 gap-4 mb-8">
          <app-date-picker
            [dateControl]="form.controls.endDateWrite"
            [dateLabel]="'pages.admin.userGroup.tabs.userGroups.dialog.endWrite' | translate"
          />
          <app-date-picker
            [dateControl]="form.controls.endDateRead"
            [dateLabel]="'pages.admin.userGroup.tabs.userGroups.dialog.endRead' | translate"
          />
        </div>

        <div class="mb-8">
          <div class="flex flex-col gap-1 mb-2">
            <h3 class="text-lg font-bold">
              {{ 'pages.admin.userGroup.tabs.userGroups.dialog.effectiveMaxValues' | translate }}
            </h3>
            <h5 class="text-xs text-shikenso-purple-base-60">
              {{
                'pages.admin.userGroup.tabs.userGroups.dialog.effectiveMaxValuesDescription'
                  | translate: { currentDay: currentDay() | date: 'dd.MM.yyyy' }
              }}
            </h5>
          </div>
          <div class="grid grid-cols-3 gap-4">
            <div>
              <span class="text-sm">{{ 'pages.admin.userGroup.tabs.userGroups.dialog.seats' | translate }}:</span>
              <span class="font-bold ml-2">{{ effectiveMaxValues().seats ?? '∞' }}</span>
            </div>
            <div>
              <span class="text-sm">{{ 'pages.admin.userGroup.tabs.userGroups.dialog.channels' | translate }}:</span>
              <span class="font-bold ml-2">{{ effectiveMaxValues().channels ?? '∞' }}</span>
            </div>
            <div>
              <span class="text-sm">
                {{ 'pages.admin.userGroup.tabs.userGroups.dialog.concurrentCampaigns' | translate }}:
              </span>
              <span class="font-bold ml-2">{{ effectiveMaxValues().campaigns ?? '∞' }}</span>
            </div>
          </div>
        </div>

        <div class="mb-8">
          <h3 class="text-lg font-bold mb-2">
            {{ 'pages.admin.userGroup.tabs.userGroups.dialog.purchases' | translate }}
          </h3>

          <form [formGroup]="purchaseForm" class="space-y-4 mb-6">
            @if (!purchaseFormVisible()) {
              <div class="flex gap-2">
                <button
                  mat-flat-button
                  color="secondary-gray-border"
                  type="button"
                  class="w-full mat-thick-button"
                  [disabled]="store.loading$() || store.saveRequested$()"
                  (click)="purchaseFormVisible.set(true)"
                >
                  @if (store.loading$() && !store.saveRequested$()) {
                    <app-dots-spinner />
                  } @else {
                    <div class="flex items-center gap-2">
                      <app-icon src="/icons/plus.svg" [size]="20" />
                      {{ 'pages.admin.userGroup.tabs.userGroups.dialog.addPurchase' | translate }}
                    </div>
                  }
                </button>

                <button
                  mat-flat-button
                  color="secondary-gray-border"
                  type="button"
                  class="w-full mat-thick-button"
                  [disabled]="store.loading$() || store.saveRequested$()"
                  (click)="onAddCampaignSprintClick()"
                >
                  @if (store.loading$() && !store.saveRequested$()) {
                    <app-dots-spinner />
                  } @else {
                    <div class="flex items-center gap-2">
                      <app-icon src="/icons/plus.svg" [size]="20" />
                      {{ 'pages.admin.userGroup.tabs.userGroups.dialog.addCampaignSprint' | translate }}
                    </div>
                  }
                </button>
              </div>
            } @else {
              <app-start-end-date-picker
                [endDateControl]="purchaseForm.controls.end"
                [initialEndDate]="purchaseForm.controls.end.value"
                [endDateLabel]="'pages.admin.userGroup.tabs.userGroups.dialog.end' | translate"
                [startDateControl]="purchaseForm.controls.start"
                [initialStartDate]="purchaseForm.controls.start.value"
                [startDateLabel]="'pages.admin.userGroup.tabs.userGroups.dialog.start' | translate"
              />

              <div class="grid grid-cols-3 gap-4">
                <mat-form-field class="w-full">
                  <mat-label>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.extraSeats' | translate }}</mat-label>
                  <input matInput type="number" formControlName="extraSeats" min="0" />
                  <mat-error [control]="purchaseForm.get('extraSeats')" />
                </mat-form-field>

                <mat-form-field class="w-full">
                  <mat-label>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.extraChannels' | translate }}</mat-label>
                  <input matInput type="number" formControlName="extraCreatorChannels" min="0" />
                  <mat-error [control]="purchaseForm.get('extraCreatorChannels')" />
                </mat-form-field>

                <mat-form-field class="w-full">
                  <mat-label>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.extraCampaigns' | translate }}</mat-label>
                  <input matInput type="number" formControlName="extraConcurrentCampaigns" min="0" />
                  <mat-error [control]="purchaseForm.get('extraConcurrentCampaigns')" />
                </mat-form-field>
              </div>

              <div class="flex w-full gap-2 justify-end">
                <button
                  mat-flat-button
                  color="secondary-transparent"
                  type="button"
                  class="mat-slim-plus-button !min-w-28"
                  [disabled]="store.loading$() || store.saveRequested$()"
                  (click)="onCancelPurchaseClick()"
                >
                  @if (store.loading$() && !store.saveRequested$()) {
                    <app-dots-spinner />
                  } @else {
                    {{ 'shared.cancel' | translate }}
                  }
                </button>

                <button
                  mat-flat-button
                  color="secondary"
                  type="button"
                  class="mat-slim-plus-button !min-w-28"
                  [disabled]="store.loading$() || store.saveRequested$()"
                  (click)="onAddPurchaseClick()"
                >
                  @if (store.loading$() && !store.saveRequested$()) {
                    <app-dots-spinner />
                  } @else {
                    <div class="flex items-center gap-2">
                      <app-icon src="/icons/plus.svg" [size]="20" />
                      {{ 'shared.add' | translate }}
                    </div>
                  }
                </button>
              </div>
            }
          </form>

          @if (localPurchases().length > 0) {
            <div class="space-y-2">
              @for (purchase of localPurchases(); track $index) {
                <div class="p-3 border border-shikenso-purple-base-12 rounded-2xl flex items-center justify-between">
                  <div class="flex flex-col gap-1 w-max text-sm">
                    <div class="flex gap-4 justify-between">
                      <div class="flex gap-1">
                        <span>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.start' | translate }}:</span>
                        <span class="font-bold">{{ purchase.startDisplay | date: 'dd.MM.yyyy HH:mm' }}</span>
                      </div>

                      @if (purchase.end) {
                        <div class="flex gap-1">
                          <span>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.end' | translate }}:</span>
                          <span class="font-bold">{{ purchase.endDisplay | date: 'dd.MM.yyyy HH:mm' }}</span>
                        </div>
                      }
                    </div>

                    <div class="flex gap-4 justify-between">
                      <div class="flex gap-1">
                        <span>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.extraSeats' | translate }}:</span>
                        <span class="font-bold">{{ purchase.extraSeats }}</span>
                      </div>
                      <div class="flex gap-1">
                        <span>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.extraChannels' | translate }}:</span>
                        <span class="font-bold">{{ purchase.extraCreatorChannels }}</span>
                      </div>
                      <div class="flex gap-1">
                        <span>{{ 'pages.admin.userGroup.tabs.userGroups.dialog.extraCampaigns' | translate }}:</span>
                        <span class="font-bold">{{ purchase.extraConcurrentCampaigns }}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    mat-icon-button
                    type="button"
                    [attr.aria-label]="'shared.delete' | translate"
                    (click)="onDeletePurchaseClick($index)"
                  >
                    <app-icon src="/icons/trash.svg" [size]="16" />
                  </button>
                </div>
              }
            </div>
          } @else {
            <p class="text-sm font-medium text-shikenso-purple-base-60">
              {{ 'pages.admin.userGroup.tabs.userGroups.dialog.noPurchases' | translate }}
            </p>
          }
        </div>
      </div>

      <div class="sticky bottom-0 bg-shikenso-base-white pt-4 pb-2 border-t border-shikenso-purple-base-12 z-10">
        <div class="flex items-center justify-end gap-4">
          <button
            mat-flat-button
            color="secondary-transparent"
            type="button"
            [disabled]="store.loading$() || store.saveRequested$()"
            (click)="onCloseClick()"
          >
            {{ 'shared.cancel' | translate }}
          </button>
          <button mat-flat-button type="button" [disabled]="store.loading$()" (click)="onSaveClick()">
            @if (store.loading$() || store.saveRequested$()) {
              <app-dots-spinner />
            } @else {
              {{ 'shared.form.save' | translate }}
            }
          </button>
        </div>
      </div>
    </form>
  </div>
</app-side-dialog>
